<template>
    <div
        ref="node"
        :class="classes"
        :data-level="node.level"
        :data-node-id="node.id"
        :data-root-idx="rootIdx"
    >
        <div v-droppable.node="onDrop" class="nodes-tree-item__content">
            <div class="nodes-tree-item__label">
                <span v-text="node.label" />
                <button class="nodes-tree-item__button" @click.prevent="deleteNode">
                    <IconCross />
                </button>
            </div>
            <div class="nodes-tree-item__edges">
                <div v-for="k in edgesKeys" :key="k" v-draggable.node="node.id" :data-edge="k" />
            </div>
            <div class="nodes-tree-item__dropzone" data-drop="before" />
            <div class="nodes-tree-item__toolbar">
                <template v-if="!editMode">
                    <button class="nodes-tree-item__button" @click.prevent="editMode = true">
                        <IconPencil />
                    </button>
                    <button class="nodes-tree-item__button" @click.prevent="addNode">
                        <IconPlus />
                    </button>
                </template>
            </div>
            <div v-if="node.start.length" class="nodes-tree-item__relations">
                <button
                    v-for="id in node.start"
                    :key="`rel-${id}`"
                    @click.prevent="deleteRelations(id)"
                >
                    <IconCross />
                </button>
            </div>
        </div>
        <NodesTree
            :nodes="node.childs"
            :root-idx="rootIdx"
            :allowed-actions="allowedActions"
            @addNode="$emit('addNode', $event)"
            @updateNode="$emit('updateNode', $event)"
            @deleteNode="$emit('deleteNode', $event)"
            @updateNodeTree="$emit('updateNodeTree', $event)"
            @deleteRelations="$emit('deleteRelations', $event)"
        />

        <portal v-if="editMode" to="modals">
            <Modal name="actions" modal-class="nodes-tree-item__modal" @closed="onModalClosed">
                <TransitionsActions :actions="allowedActions" @selected="onSelected" />
            </Modal>
        </portal>
    </div>
</template>

<script>
import { Modal } from '@speechki-frontend/shared/ui/modals';
import { IconCross, IconPencil, IconPlus } from '@speechki-frontend/shared/ui/icons';

export default {
    name: 'NodesTreeItem',
    components: {
        NodesTree: () => import('./NodesTree'),
        TransitionsActions: () => import('../../components/TransitionsActions/TransitionsActions'),
        IconCross,
        IconPencil,
        IconPlus,
        Modal,
    },
    props: {
        node: {
            type: Object,
            required: true,
        },
        rootIdx: {
            type: Number,
            default: 0,
        },
        allowedActions: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            edgesKeys: ['bottom'],
            editMode: false,
            dragMode: false,
            relations: [],
        };
    },
    computed: {
        classes() {
            return {
                'nodes-tree-item': true,
                'nodes-tree-item--editable': this.editMode,
                'nodes-tree-item--created': this.node.created,
                'nodes-tree-item--dragged': this.dragMode,
            };
        },
    },
    mounted() {
        const { editable = false } = this.node;
        this.editMode = editable;
    },
    methods: {
        applyChanges() {
            this.editMode = false;
        },
        addNode() {
            const { id } = this.node;
            this.$emit('addNode', { depends_on: [id] });
        },
        deleteNode() {
            const { id } = this.node;
            this.$emit('deleteNode', { id });
        },
        async deleteRelations(relId) {
            let { id } = this.node;
            const child = this.node.childs.find(({ id }) => id === relId);

            this.$emit('deleteRelations', { id: relId, dependId: id });
            await this.$nextTick();
            this.$emit('deleteRelations', { id, startId: relId });

            if (child) {
                await this.$nextTick();
                this.$emit('updateNode', {
                    id,
                    payload: {
                        depends_on: [],
                        overwrite: true,
                    },
                });
            }
        },
        onSelected({ action }) {
            const { id } = this.node;
            const payload = { action };
            this.$emit('updateNode', { id, payload });
            this.applyChanges();
        },
        onModalClosed() {
            this.applyChanges();

            if (this.node.label) {
                this.$emit('updateNode', { id: this.node.id });
            } else {
                this.deleteNode();
            }
        },
        async onDrop(event) {
            const id = event.replace(/"/g, '');

            this.$emit('updateNode', { id: this.node.id, payload: { depends_on: [id] } });
            await this.$nextTick();
            this.$emit('updateNode', { id, payload: { start: [this.node.id] } });
        },
    },
};
</script>
